import { useState, useCallback } from 'react';

export interface Class {
  id: string;
  title: string;
  description?: string;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
  startDate: string;
  endDate: string;
  enrollmentCount: number;
  enrollmentLimit?: number;
  courseTitle: string;
  instructor: string;
  courseId: string;
  teacherId: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Course {
  id: string;
  title: string;
  description: string;
  subject: string;
  level: string;
  duration: {
    value: number;
    unit: string;
  };
  mode: string;
  gradingPolicy: string;
}

export interface Teacher {
  id: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
}

export interface Student {
  id: string;
  name: string;
  email: string;
  enrollmentDate?: string;
}

export interface ClassFormData {
  title: string;
  description: string;
  courseId: string;
  teacherId: string;
  startDate: string;
  endDate: string;
  enrollmentLimit?: number;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
}

// Mock data for development
const mockClasses: Class[] = [
  
  {
    id: '2',
    title: 'Quy trình An toàn Mặt đất Sân bay - Ca A',
    description: 'Khóa đào tạo về quy trình an toàn tại mặt đất sân bay',
    status: 'upcoming',
    startDate: '2025-02-01',
    endDate: '2025-02-22',
    enrollmentCount: 22,
    enrollmentLimit: 30,
    courseTitle: 'Quy trình An toàn Mặt đất Sân bay',
    instructor: 'Nguyễn Văn An',
    courseId: 'course-2',
    teacherId: 't2',
  },
  {
    id: '3',
    title: 'Ứng phó Khẩn cấp Sân bay - Đào tạo Cấp bách',
    description: 'Đào tạo xử lý các tình huống khẩn cấp tại sân bay',
    status: 'active',
    startDate: '2025-01-20',
    endDate: '2025-02-10',
    enrollmentCount: 15,
    enrollmentLimit: 20,
    courseTitle: 'Ứng phó Khẩn cấp Sân bay',
    instructor: 'Hoàng Văn Dũng',
    courseId: 'course-3',
    teacherId: 't3',
  },
];

const mockCourses: Course[] = [
  
  {
    id: 'course-2',
    title: 'Quy trình An toàn Mặt đất Sân bay',
    description: 'Khóa đào tạo an toàn hàng không',
    subject: 'Aviation Safety',
    level: 'Advanced',
    duration: { value: 3, unit: 'week' },
    mode: 'live',
    gradingPolicy: 'test_average',
  },
  {
    id: 'course-3',
    title: 'Ứng phó Khẩn cấp Sân bay',
    description: 'Đào tạo xử lý tình huống khẩn cấp',
    subject: 'Emergency Response',
    level: 'Intermediate',
    duration: { value: 2, unit: 'week' },
    mode: 'blended',
    gradingPolicy: 'weighted',
  },
];

const mockTeachers: Teacher[] = [
  { id: 't1', name: 'Jane Smith', email: 'jane.smith@example.com', role: 'Professor', department: 'Mathematics' },
  { id: 't2', name: 'Nguyễn Văn An', email: 'nguyen.van.an@example.com', role: 'Senior Instructor', department: 'Aviation Safety' },
  { id: 't3', name: 'Hoàng Văn Dũng', email: 'hoang.van.dung@example.com', role: 'Instructor', department: 'Emergency Response' },
  { id: 't4', name: 'Phạm Thị Lan', email: 'pham.thi.lan@example.com', role: 'Assistant Professor', department: 'Customer Service' },
];

export const useClass = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all classes
  const fetchClasses = useCallback(async (courseId?: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Remove artificial delay - return mock data immediately
      let filteredClasses = mockClasses;
      if (courseId) {
        filteredClasses = mockClasses.filter(cls => cls.courseId === courseId);
      }
      
      setClasses(filteredClasses);
      return filteredClasses;
    } catch (err) {
      const errorMessage = 'Failed to fetch classes';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch class by ID
  const fetchClassById = useCallback(async (id: string): Promise<Class | null> => {
    console.log('fetchClassById called with id:', id);
    setError(null);
    
    try {
      // Return mock data immediately without setting loading states
      console.log('Searching in mockClasses:', mockClasses.map(c => c.id));
      const classData = mockClasses.find(cls => cls.id === id) || null;
      console.log('Found classData:', classData);
      setSelectedClass(classData);
      return classData;
    } catch (err) {
      console.error('Error in fetchClassById:', err);
      const errorMessage = 'Failed to fetch class';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  // Create new class
  const createClass = useCallback(async (classData: ClassFormData): Promise<Class> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Remove artificial delay - return mock data immediately
      
      // Find course and teacher info
      const course = mockCourses.find(c => c.id === classData.courseId);
      const teacher = mockTeachers.find(t => t.id === classData.teacherId);
      
      const newClass: Class = {
        id: `class-${Date.now()}`,
        title: classData.title,
        description: classData.description,
        status: classData.status,
        startDate: classData.startDate,
        endDate: classData.endDate,
        enrollmentCount: 0,
        enrollmentLimit: classData.enrollmentLimit,
        courseTitle: course?.title || 'Unknown Course',
        instructor: teacher?.name || 'Unknown Instructor',
        courseId: classData.courseId,
        teacherId: classData.teacherId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      // Add to mock data (in real app, this would be handled by the API)
      mockClasses.push(newClass);
      setClasses(prev => [...prev, newClass]);
      
      return newClass;
    } catch (err) {
      const errorMessage = 'Failed to create class';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update class
  const updateClass = useCallback(async (id: string, updates: Partial<ClassFormData>): Promise<Class> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Remove artificial delay - return mock data immediately
      
      const classIndex = mockClasses.findIndex(cls => cls.id === id);
      if (classIndex === -1) {
        throw new Error('Class not found');
      }
      
      // Find course and teacher info if they were updated
      const course = updates.courseId ? mockCourses.find(c => c.id === updates.courseId) : undefined;
      const teacher = updates.teacherId ? mockTeachers.find(t => t.id === updates.teacherId) : undefined;
      
      const updatedClass: Class = {
        ...mockClasses[classIndex],
        ...updates,
        courseTitle: course?.title || mockClasses[classIndex].courseTitle,
        instructor: teacher?.name || mockClasses[classIndex].instructor,
        updatedAt: new Date().toISOString(),
      };
      
      // Update mock data
      mockClasses[classIndex] = updatedClass;
      setClasses(prev => prev.map(cls => cls.id === id ? updatedClass : cls));
      setSelectedClass(updatedClass);
      
      return updatedClass;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update class';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete class
  const deleteClass = useCallback(async (id: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Remove artificial delay - return mock data immediately
      
      const classIndex = mockClasses.findIndex(cls => cls.id === id);
      if (classIndex === -1) {
        throw new Error('Class not found');
      }
      
      // Remove from mock data
      mockClasses.splice(classIndex, 1);
      setClasses(prev => prev.filter(cls => cls.id !== id));
      
      if (selectedClass?.id === id) {
        setSelectedClass(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete class';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [selectedClass]);

  // Get available courses
  const fetchCourses = useCallback(async (): Promise<Course[]> => {
    // Remove artificial delay - return mock data immediately
    return mockCourses;
  }, []);

  // Get available teachers
  const fetchTeachers = useCallback(async (): Promise<Teacher[]> => {
    // Remove artificial delay - return mock data immediately
    return mockTeachers;
  }, []);

  // Get course by ID
  const fetchCourseById = useCallback(async (id: string): Promise<Course | null> => {
    console.log('fetchCourseById called with id:', id);
    // Return mock data immediately without any loading states
    console.log('Searching in mockCourses:', mockCourses.map(c => c.id));
    const courseData = mockCourses.find(course => course.id === id) || null;
    console.log('Found courseData:', courseData);
    return courseData;
  }, []);

  return {
    // State
    classes,
    selectedClass,
    isLoading,
    error,
    
    // Actions
    fetchClasses,
    fetchClassById,
    createClass,
    updateClass,
    deleteClass,
    fetchCourses,
    fetchTeachers,
    fetchCourseById,
    
    // Utility
    setSelectedClass,
    setError,
  };
}; 